import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listArchivedDeals, unarchiveDeal, deleteDeal, getMe, isAdmin } from '../lib/api.js';
import { Card, PageHeader, Pill, Skeleton } from '../components/ui.js';

/**
 * The archive — admin only.
 *
 * Anyone on the team can archive a deal off the board, which is deliberately low-stakes because
 * it's reversible. This page is where that decision gets reviewed: an admin either restores the
 * deal or deletes it for good. Permanent delete exists nowhere else in the app.
 *
 * The admin check here only hides the UI; /api/deals/archived and DELETE /api/deals/:id both
 * enforce the same rule server-side.
 */
export function Archive() {
  const qc = useQueryClient();
  const { data: meData, isLoading: meLoading } = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false, staleTime: 300_000 });
  const admin = isAdmin(meData?.identity);

  const { data, isLoading, error } = useQuery({
    queryKey: ['archivedDeals'],
    queryFn: listArchivedDeals,
    enabled: admin, // don't fire a request we know will 403
    retry: 1,
  });

  // Which row is mid-action, so only that row's buttons show a pending state.
  const [busyId, setBusyId] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const done = () => {
    setBusyId(null);
    qc.invalidateQueries({ queryKey: ['archivedDeals'] });
    qc.invalidateQueries({ queryKey: ['deals'] });
  };
  const restore = useMutation({
    mutationFn: (id: string) => unarchiveDeal(id),
    onSuccess: done,
    onError: (e) => { setBusyId(null); setFailed(`Restore failed: ${(e as Error).message}`); },
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteDeal(id),
    onSuccess: done,
    onError: (e) => { setBusyId(null); setFailed(`Delete failed: ${(e as Error).message}`); },
  });

  if (meLoading) return <div className="max-w-[1000px]"><Skeleton h={80} /></div>;

  if (!admin) {
    return (
      <div className="max-w-[1000px]">
        <PageHeader eyebrow="Tools" title="Archive" />
        <Card>
          <p className="text-[12.5px] text-muted">
            The archive is visible to admins only. If you archived a deal by mistake, ask an admin to restore it — nothing is deleted by archiving.
          </p>
        </Card>
      </div>
    );
  }

  const rows = data?.deals ?? [];

  return (
    <div className="max-w-[1000px]">
      <PageHeader
        eyebrow="Tools"
        title="Archive"
        desc="Deals the team has taken off the board. Nothing here is deleted — restore it, or delete it permanently."
      />

      {failed && (
        <div className="mb-3.5 rounded-lg border border-red/30 bg-red-50 px-4 py-2.5 text-[11.5px] text-red">{failed}</div>
      )}

      {error ? (
        <Card><p className="text-[12.5px] text-muted">{String((error as Error).message)}</p></Card>
      ) : (
        <Card bodyClass="p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[12.5px] min-w-[720px]">
              <thead>
                <tr>{['Deal', 'Sector', 'Stage', 'Archived', 'By', ''].map((h) => (
                  <th key={h} className="text-left text-muted font-bold text-[10.5px] uppercase px-4 py-2.5 border-b border-line-2">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {isLoading && Array.from({ length: 4 }).map((_, r) => (
                  <tr key={`sk-${r}`} aria-hidden="true">{Array.from({ length: 6 }).map((_, c) => (
                    <td key={c} className="px-4 py-2.5 border-b border-line-2"><Skeleton w={c === 0 ? 160 : 80} h={11} /></td>
                  ))}</tr>
                ))}

                {!isLoading && rows.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-muted">Nothing archived. Deals the team archives show up here for you to restore or delete.</td></tr>
                )}

                {rows.map((d) => {
                  const busy = busyId === d.id;
                  return (
                    <tr key={d.id} className="hover:bg-canvas align-middle">
                      <td className="px-4 py-2.5 border-b border-line-2">
                        <Link to={`/deals/${d.id}`} className="font-semibold text-navy hover:text-teal-d">{d.company}</Link>
                        <span className="text-muted-2"> · {d.short}</span>
                      </td>
                      <td className="px-4 py-2.5 border-b border-line-2 text-muted">{d.sector}</td>
                      <td className="px-4 py-2.5 border-b border-line-2"><Pill tone="gray">{d.status}</Pill></td>
                      <td className="px-4 py-2.5 border-b border-line-2 text-muted-2 whitespace-nowrap">{new Date(d.archivedAt).toLocaleDateString()}</td>
                      <td className="px-4 py-2.5 border-b border-line-2 text-muted">{d.archivedBy ?? '—'}</td>
                      <td className="px-4 py-2.5 border-b border-line-2">
                        <div className="flex gap-2 justify-end">
                          <button
                            disabled={busy}
                            onClick={() => { setFailed(null); setBusyId(d.id); restore.mutate(d.id); }}
                            className="text-[11px] font-semibold px-2 py-1 rounded border border-line text-navy hover:bg-canvas disabled:opacity-50">
                            {busy && restore.isPending ? 'Restoring…' : '↩ Restore'}
                          </button>
                          <button
                            disabled={busy}
                            onClick={() => {
                              setFailed(null);
                              if (!confirm(`Permanently delete ${d.company}?\n\nThis destroys the deal and every call, note, document, discussion message and IC vote attached to it. It cannot be undone — restore it instead if you're not sure.`)) return;
                              setBusyId(d.id);
                              del.mutate(d.id);
                            }}
                            className="text-[11px] font-semibold px-2 py-1 rounded border border-red/40 text-red hover:bg-red hover:text-white disabled:opacity-50">
                            {busy && del.isPending ? 'Deleting…' : '🗑 Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
